import { createContext, useState } from 'react'
import themeConfig from '../../theme.config'

export const MarkupContext = createContext({})

const lightOrDark = (values, color) => {
  if (!values) {
    return
  }
  const [r, g, b] = color.replace('rgb(', '').replace(')', '').split(',').map(part => parseInt(part))

  // http://alienryderflex.com/hsp.html
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b))

  // Using the HSP value, determine whether the color is light or dark
  return values[hsp > 127.5 ? 'light' : 'dark']
}

const colors = {
  transparent: 'rgb(255,255,255)'
}

export function MarkupProvider({children}) {
  const [classes, setClasses] = useState([])

  const getPaletteColor = (theme, shade) => {
    let themeShade = theme
    if (shade) {
      themeShade += '-'+shade
    } else if (theme.includes('-')) {
      [theme, shade] = theme.split('-')
    }
    let value = colors[themeShade]
    if (value === undefined) {
      const div = document.createElement('div')
      const offset = shade !== undefined ? (shade > 500 ? 11 : 8) * (500 - shade) / 100 : 0
      div.style.color = 'hsl(from '+(theme[0] == '#' ? theme : 'var(--color-'+theme+')')+' h s calc(l + '+offset+'))'
      document.body.appendChild(div)
      const color = window.getComputedStyle(div).getPropertyValue('color')
      div.remove()

      if (color.startsWith('color(srgb ')) {
        if (color.endsWith(' / 0)')) {
          value = 'rgb(255,255,255)'
        } else {
          value = 'rgb('+color.replace('color(srgb ', '').split(' / ')[0].split(' ').map(part => Math.round(Math.min(1, parseFloat(part.replace(/[^0-9\.]+/, ''))) * 255).toString()).join(',')+')'
        }
      } else {
        value = color.replace(/ /g, '')
      }
      colors[themeShade] = value
    }
    return value
  }
  
  const detectPaletteClass = className => {
    const newClassNames = className.split(' ').filter(newClassName => {
      return newClassName.match(/(bg|outline|text|border|decoration|divide)\-[^\[]/) &&
          !['text-current', 'text-xs', 'text-sm', 'text-base', 'text-lg', 'text-xl', 'text-2xl', 'text-left', 'text-center'].includes(newClassName) &&
          !newClassName.includes('-undefined')
    })
    if (newClassNames.some(newClassName => !classes.includes(newClassName))) {
      setTimeout(() => {
        setClasses(previousClasses => {
          if (newClassNames.every(newClassName => previousClasses.includes(newClassName))) {
            return previousClasses
          }
          return [...new Set([...previousClasses, ...newClassNames])]
        })
      })
    }
    return className
  }

  const createMarkup = markup => {
    if (!markup) {
      return
    }

    // Parse palette classes
    for (const m of markup.matchAll(/(class="([^"]+)")/g)) {
      detectPaletteClass(m[2])
    }

    let el = document.createElement('div')
    el.innerHTML = markup

    // Table classes
    el.classList.add(el.querySelectorAll('table').length > 0 ? 'has-table' : 'no-table')
  
    // External aria
    const links = el.querySelectorAll('a')
    Array.from(links).forEach((link) => {
      if (link.target === '_blank') {
        link.setAttribute('aria-label', 'link opens in new tab or window')
      }
    })
    return {
      __html: el[el.classList.contains('has-table') ? 'outerHTML' : 'innerHTML'].toString()
    }
  }

  const css = classes.map(className => {
    let variant = ''
    let classNameWithoutVariant = className
    if (className.includes(':')) {
      const classNameParts = className.split(':')
      classNameWithoutVariant = classNameParts.pop()
      variant = classNameParts.join(':')+':'
    }
    const parts = classNameWithoutVariant.split('-')
    const type = parts[0]
    const theme = parts[1]
    let shade
    let modifier
    if (parts[2]) {
      if (parts[2].match(/^[0-9]+$/g)) {
        shade = parts[2]
        modifier = parts[3]
      } else {
        modifier = parts[2]
      }
    }

    let offset
    if (shade !== undefined) {
      const shadeInt = parseInt(parts[2])
      offset = (shadeInt > 500 ? 11 : 8) * (500 - shadeInt) / 100
    } else {
      offset = 0
    }

    let inner
    if (modifier) {
      if (!themeConfig.detectInverseText) {
        return
      }

      const colors = themeConfig.colors || {}
      const values = {
        text: colors.text,
        link: colors.link.default,
        hover: colors.link.hover
      }[modifier]

      const value = getPaletteColor(theme, shade)

      const invertedThemeShade = lightOrDark(values, value)
      if (!invertedThemeShade) {
        return
      }
      inner = 'color: '+getPaletteColor(invertedThemeShade)
    } else {
      inner = {
        bg: 'background-color',
        outline: 'outline-color',
        text: 'color',
        border: 'border-color',
        decoration: 'text-decoration-color',
        divide: 'border-color'
      }[type]+':hsl(from '+(theme[0] == '#' ? theme : 'var(--color-'+theme+')')+' h s calc(l + '+offset+'))'
    }

    let output
    let selector = type == 'divide' ? '>*+*' : ''
    if (variant == '') {
      if (className.includes('#')) {
        output = ['[class^="'+className+'"]', '[class*=" '+className+'"]'].map(attribute => {
          return attribute+selector+'{'+inner+'}'
        }).join('')
      } else {
        output = '.'+className+selector+'{'+inner+'}'
      }
    } else {
      if (variant == 'group-hover:') {
        selector = ':is(:where(.group):hover *)'+selector
      } else if (variant == 'hover:') {
        selector = ':hover'+selector
      } else if (variant == 'hover:lg:') {
        selector = ':hover{@media(width >= 64rem)'+selector
      } else if (variant == 'focus-visible:') {
        selector = ':focus-visible'+selector
      } else if (variant == 'max-md:') {
        selector += '{@media(width < 48rem)'
      } else if (variant == 'md:') {
        selector += '{@media(width >= 48rem)'
      } else if (variant == 'max-lg:') {
        selector += '{@media(width < 64rem)'
      } else if (variant == 'lg:') {
        selector += '{@media(width >= 64rem)'
      } else if (variant == 'before:') {
        selector = ':before'+selector
      } else if (variant == 'after:') {
        selector = ':after'+selector
      }
      output = ['[class^="'+className+'"]', '[class*=" '+className+'"]'].map(attribute => {
        return attribute+selector+'{'+inner+(selector.includes('{') ? '}}' : '}')
      }).join('')
    }
    return output
  }).join('')

  return <MarkupContext.Provider value={{
    createMarkup,
    detectPaletteClass,
    getPaletteColor
  }}>
    {children}
    {css && <style type="text/css">{css}</style>}
  </MarkupContext.Provider>
}